import {
  ArrowRight,
  Clock3,
  FolderGit2,
  ListChecks,
  Plus,
  Target,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getStudentProjects } from '../api.js'

function safeText(value, fallback = '暂无说明') {
  const text = String(value || '').trim()
  if (!text || text === 'undefined' || text === 'null') return fallback
  return text
}

function statusLabel(status) {
  if (status === 'completed' || status === 'done') return '已完成'
  if (status === 'paused') return '已暂停'
  if (status === 'archived') return '已归档'
  return '进行中'
}

function normalizeProject(item) {
  return {
    id: String(item.id || ''),
    title: safeText(item.title, '未命名项目'),
    summary: safeText(item.summary, '从项目成长计划沉淀的项目成果。'),
    projectGoal: safeText(item.projectGoal || item.project_goal, '完成一个可展示、可写进简历的项目成果。'),
    status: safeText(item.status, 'active'),
    progress: Math.max(0, Math.min(Number(item.progress || 0), 100)),
    taskCount: Number(item.taskCount || item.task_count || item.tasks?.length || 0),
    doneCount: Number(item.doneCount || item.done_count || 0),
    updatedAt: item.updatedAt || item.updated_at || item.createdAt || item.created_at || null,
  }
}

function dateText(value) {
  if (!value) return '暂无更新时间'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '暂无更新时间'
  return parsed.toLocaleDateString('zh-CN')
}

function StudentProjectsPage() {
  const [state, setState] = useState({ loading: true, error: '', items: [] })

  useEffect(() => {
    const controller = new AbortController()
    setState((current) => ({ ...current, loading: true, error: '' }))
    getStudentProjects(controller.signal)
      .then((payload) => {
        const items = Array.isArray(payload?.items) ? payload.items.map(normalizeProject) : []
        setState({ loading: false, error: '', items })
      })
      .catch((error) => {
        setState({
          loading: false,
          error: error?.message || '项目成果加载失败，请稍后重试。',
          items: [],
        })
      })
    return () => controller.abort()
  }, [])

  const projects = useMemo(() => state.items.filter((item) => item.id), [state.items])

  return (
    <main className="page-container student-projects-page">
      <section className="student-projects-hero">
        <div>
          <span><FolderGit2 size={18} /> 我的项目成果</span>
          <h1>我的项目成果中心</h1>
          <p>查看项目成长计划沉淀下来的项目、任务和成果，为后续简历优化准备真实材料。</p>
        </div>
        <Link className="primary-button" to="/projects/recommendations">
          <Plus size={16} /> 开始新项目
        </Link>
      </section>

      {state.error ? (
        <section className="student-projects-notice">
          <strong>暂时无法加载项目成果</strong>
          <p>{state.error}</p>
          <Link className="secondary-button" to="/projects/recommendations">去创建项目成长计划</Link>
        </section>
      ) : null}

      <section className="student-projects-grid">
        {state.loading ? (
          <article className="student-project-result-card">
            <Clock3 size={18} />
            <strong>正在加载我的项目...</strong>
            <p>项目成长计划保存后，会自动出现在这里。</p>
          </article>
        ) : null}

        {!state.loading && projects.length === 0 ? (
          <article className="student-project-result-card is-empty">
            <Target size={20} />
            <strong>还没有项目成果</strong>
            <p>先从项目成长计划选择一个项目，加入成长中心后会自动创建成果记录。</p>
            <Link className="primary-button" to="/projects/recommendations">进入项目成长计划</Link>
          </article>
        ) : null}

        {projects.map((project) => (
          <article className="student-project-result-card" key={project.id}>
            <div className="student-project-result-head">
              <span>{statusLabel(project.status)}</span>
              <small>{dateText(project.updatedAt)}</small>
            </div>
            <h2>{project.title}</h2>
            <p>{project.summary}</p>
            <div className="student-project-progress" aria-label={`完成进度 ${project.progress}%`}>
              <i style={{ width: `${project.progress}%` }} />
            </div>
            <div className="student-project-result-meta">
              <span><ListChecks size={15} /> {project.doneCount}/{project.taskCount || 0} 任务</span>
              <strong>{project.progress}%</strong>
            </div>
            <section>
              <span>项目目标</span>
              <p>{project.projectGoal}</p>
            </section>
            <Link className="secondary-button" to={`/projects/${project.id}`}>
              查看详情 <ArrowRight size={16} />
            </Link>
          </article>
        ))}
      </section>
    </main>
  )
}

export default StudentProjectsPage
